<template>
    <div class="search-container">
        <div v-if="activeFilters.length" class="search-wrapper">
            <div class="search">
                <div class="search-input">
                    <div class="form-group">
                        <vSelect v-if="activeFilters.includes('rubrik')" v-model="selectedTags" :options="tags"
                                 label="title"
                                 :placeholder="translate(translations, 'location.filterby')"/>
                        <vSelect v-if="activeFilters.includes('services')" v-model="selectedServices"
                                 :options="services" label="title"
                                 :placeholder="translate(translations, 'location.serviceplaceholder')"/>
                        <div v-if="activeFilters.includes('zip')" class="input-group">
                            <input
                                id="map-zip-input" v-model="zip" type="text"
                                :placeholder="translate(translations, 'location.zip')"
                                @keyup.enter="getFilteredLocations"
                                @input="resetFilterIfNecessary"
                            >
                            <button v-if="zip.length" class="rest-btn" type="button" @click="resetFilter(false)">
                                x
                            </button>
                        </div>
                        <button class="btn btn-primary filter-btn" @click="getFilteredLocations">
                            <span class="icon after i-settings">{{
                                    translate(translations, 'location.filterlocations')
                                }}</span>
                        </button>
                    </div>
                    <div class="error">
                        <label v-if="errorMessage" for="map-zip-input" class="error-label">{{ errorMessage }}</label>
                    </div>
                </div>
            </div>
        </div>
    </div>
    <div class="company-tile-list">
        <CompanyListEntry
            v-for="(location, index) in sortedLocations" :key="index" :location="location"
            :icon-path="iconPath" :translations="translations"
        />
    </div>
</template>


<script>
import $ from 'jquery';
import { computed, ref } from 'vue';
import vSelect from 'vue-select';
import { translate } from '../translate';
import CompanyListEntry from './CompanyListEntry.vue';
import OpenIndicator from './OpenIndicator.vue';

export default {
	name: 'CompanyList',
	components: { CompanyListEntry, vSelect },
	props: {
		locations: {
			type: Object,
			default: () => ({}),
		},
		iconPath: {
			type: String,
			required: true,
		},
		filterUrl: {
			type: String,
			required: true,
		},
		translations: {
			type: Object,
			required: true,
		},
		filterConfig: {
			type: Object,
			required: true,
		},
		defaultSearchZipLocation: {
			type: Number,
			default: null,
		},
	},
	setup(props) {
		vSelect.props.components.default = () => ({ OpenIndicator });
		const zip = ref('');
		const errorMessage = ref('');
		const isLoading = ref(false);
		const locationZip = ref('');
		const locationUids = Object.values(props.locations).map(
			(location) => location.uid,
		);
		const selectedService = ref('');
		const selectedTags = ref('');
		const activeFilters = Object.values(props.filterConfig);
		const zipFilterActive = ref(false);

		const defaultLocations = ref(
			Object.values(props.locations).reduce((acc, location) => {
				acc[location.uid] = location;
				return acc;
			}, {}),
		);

		const services = Object.values(defaultLocations.value).reduce(
			(acc, location) => {
				for (const service of location.services) {
					if (!acc.includes(service)) {
						acc.push(service);
					}
				}
				return acc;
			},
			[],
		);

		const tags = Object.values(defaultLocations.value).reduce(
			(acc, location) => {
				for (const tag of location.tags) {
					if (!acc.includes(tag)) {
						acc.push(tag);
					}
				}
				return acc;
			},
			[],
		);

		/**
		 * `filteredLocations` is a computed property that dynamically calculates and returns
		 * a filtered list of locations based on selected services, tags, and zip codes.
		 * It filters the locations by checking if they meet the selected criteria:
		 * - If a service is selected, only locations including this service are considered.
		 * - If tags are selected, only locations including at least one of these tags are considered.
		 * - If zip filter is active, only locations with matching zip codes are considered.
		 * Additionally, if the zip filter is active and no locations match the provided zip,
		 */
		const filteredLocations = computed(() => {
			const locations = Object.values(defaultLocations.value).filter(
				(location) => {
					const serviceIncluded = selectedService.value
						? location.services.includes(selectedService.value)
						: true;
					const tagIncluded = selectedTags.value
						? location.tags.some((tag) => selectedTags.value.includes(tag))
						: true;
					const zipIncluded = zipFilterActive.value
						? location.searchZipCodes.length === 0 ||
							location.searchZipCodes.includes(zip.value)
						: true;

					return serviceIncluded && tagIncluded && zipIncluded;
				},
			);

			if (
				zipFilterActive.value &&
				!locations.some((location) =>
					location.searchZipCodes.includes(zip.value),
				)
			) {
				locations.push(defaultLocations.value[props.defaultSearchZipLocation]);
			}

			return locations;
		});

		const sortedLocations = computed(() => {
			return Object.values(filteredLocations.value).sort((a, b) => {
				return a.distance - b.distance;
			});
		});

		const getFilteredLocations = () => {
			if (!activeFilters.includes('zip')) {
				return;
			}

			isLoading.value = true;
			errorMessage.value = '';
			$.ajax({
				url: props.filterUrl,
				data: {
					'tx_atllocations_ajaxinterface[radius]': 20000,
					'tx_atllocations_ajaxinterface[zip]': zip.value,
					'tx_atllocations_ajaxinterface[locationsToInclude]': locationUids,
					'tx_atllocations_ajaxinterface[includeDisposalLocations]': 1,
				},
				success: (data) => {
					isLoading.value = false;
					if (data.status !== 'error') {
						zipFilterActive.value = true;
						for (const locationData of Object.values(data.locations)) {
							const location = Object.values(defaultLocations.value).find(
								(loc) => loc.uid === locationData.uid,
							);
							if (location) {
								location.distance = locationData.distance;
							}
						}
					}
					if (data.status === 'error' && data.code === '404') {
						errorMessage.value = translate(
							props.translations,
							'location.zipnotfound',
						);
					}
				},
			});
		};

		const resetFilter = (keepValue) => {
			zip.value = keepValue ? zip.value : '';
			zipFilterActive.value = false;
			errorMessage.value = '';
			for (const location of Object.values(defaultLocations.value)) {
				location.distance = 0;
			}
		};

		const resetFilterIfNecessary = () => {
			if (zipFilterActive.value) {
				resetFilter(true);
			}
		};

		return {
			zip,
			errorMessage,
			isLoading,
			getFilteredLocations,
			sortedLocations,
			resetFilter,
			translate,
			locationZip,
			activeFilters,
			services,
			selectedServices: selectedService,
			selectedTags,
			tags,
			resetFilterIfNecessary,
		};
	},
};
</script>
